import diacritics from "diacritics";
import React, { useMemo, useState } from "react";

import useEventCallback from "@/mk2/hooks/useEventCallback";

function toSearchText(text: string) {
    if (!text) {
        return "";
    }
    return (
        diacritics
            .remove(text)
            // [^\w] everything that is not a digit, word or
            // underscore
            .replace(/[^\w]/g, "")
    );
}

function normalizeSearch(search: string) {
    const qStripped = toSearchText(search);
    const anyChar = ".*";
    return new RegExp(anyChar + qStripped + anyChar, "i");
}

interface Option {
    value: string | number;
    filterValue: string;
}

interface Options<TOption extends Option> {
    options: TOption[];
    alwaysDisplayed?: Array<TOption["value"]>;

    isSelected(option: TOption): boolean;
}

interface Returned<TOption extends Option> {
    options: TOption[];
    filterValue: string;

    onFilterClear();
    onFilterChange(event: React.FormEvent<HTMLInputElement>);
}

export default function useFacetFiltering<TOption extends Option = Option>({
    options,
    alwaysDisplayed,
    isSelected,
}: Options<TOption>): Returned<TOption> {
    const [initiallySelected] = useState(() => options.filter(isSelected));
    const [filterValue, setFilterValue] = useState<string>("");

    const onFilterClear = useEventCallback(() => {
        setFilterValue("");
    }, [setFilterValue]);

    const onFilterChange = useEventCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setFilterValue(event.currentTarget.value);
        },
        [setFilterValue],
    );

    const filteredOptions: TOption[] = useMemo(() => {
        const searchText = normalizeSearch(filterValue);
        return [
            ...initiallySelected,
            ...options.filter(
                (opt) =>
                    !initiallySelected.find((o) => o.value === opt.value) &&
                    (searchText.test(toSearchText(opt.filterValue)) ||
                        isSelected(opt) ||
                        alwaysDisplayed?.includes(opt.value)),
            ),
        ];
    }, [filterValue, options, isSelected, alwaysDisplayed, initiallySelected]);

    return {
        options: filteredOptions,
        onFilterClear,
        onFilterChange,
        filterValue,
    };
}
